import React, { ReactNode } from 'react'
import { Box, Collapse, Divider, List, ListItemButton, Typography } from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { AdvancedPatientSummary } from '../api/types'
import { drawerCardSx } from '../components/Section'
import { onDark } from '../theme'
import { CaseloadGroup, CaseloadModule } from './caseloadModules'

type Props = {
  modules: CaseloadModule[]
  collapsed: ReadonlySet<string>
  onToggle: (key: string) => void
  renderRow: (summary: AdvancedPatientSummary) => ReactNode
}

const themeKey = (caseloadModule: CaseloadModule, group: CaseloadGroup) => `${caseloadModule.key}/${group.key}`

const capsSx = { fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' } as const

const rowsSx = {
  '& > .MuiListItemButton-root': { borderBottom: `1px solid ${onDark.line}` },
  '& > .MuiListItemButton-root:last-child': { borderBottom: 'none' },
}

type ThemeGroupProps = {
  group: CaseloadGroup
  expanded: boolean
  onToggle: () => void
  renderRow: Props['renderRow']
}

// The info drawer's section card and header (`components/Section`), holding the theme's rows.
const ThemeGroup = ({ group, expanded, onToggle, renderRow }: ThemeGroupProps) => (
  <Box sx={{ ...drawerCardSx, mx: 2, mb: 2 }}>
    <ListItemButton
      aria-expanded={expanded}
      onClick={onToggle}
      sx={{
        gap: 1,
        px: 2,
        minHeight: 36,
        py: 0.5,
        color: onDark.secondary,
        borderBottom: expanded ? `1px solid ${onDark.line}` : 'none',
      }}
    >
      <Typography component="h3" sx={{ ...capsSx, flex: 1, fontSize: 15, color: onDark.primary }}>
        {group.label}
      </Typography>
      <Typography sx={{ fontSize: 14, color: onDark.secondary }}>{group.summaries.length}</Typography>
      {expanded ? (
        <ExpandLess sx={{ fontSize: 22 }} titleAccess="collapse" />
      ) : (
        <ExpandMore sx={{ fontSize: 22 }} titleAccess="expand" />
      )}
    </ListItemButton>
    <Collapse in={expanded} timeout="auto" unmountOnExit>
      <List disablePadding sx={rowsSx}>
        {group.summaries.map(renderRow)}
      </List>
    </Collapse>
  </Box>
)

const AdvancedCaseloadGroups = ({ modules, collapsed, onToggle, renderRow }: Props) => (
  <Box sx={{ pb: 0.5 }}>
    {modules.map((caseloadModule, index) => (
      <Box key={caseloadModule.key} component="section" aria-label={caseloadModule.label}>
        <Divider
          textAlign="center"
          sx={{
            ...capsSx,
            pt: index === 0 ? 2 : 4,
            pb: 1.5,
            px: 2,
            fontSize: 13,
            color: onDark.primary,
            '&::before, &::after': { borderColor: onDark.line },
          }}
        >
          {caseloadModule.label}
        </Divider>
        {caseloadModule.groups.map((group) => (
          <ThemeGroup
            key={group.key}
            group={group}
            expanded={!collapsed.has(themeKey(caseloadModule, group))}
            onToggle={() => onToggle(themeKey(caseloadModule, group))}
            renderRow={renderRow}
          />
        ))}
      </Box>
    ))}
  </Box>
)

export default AdvancedCaseloadGroups
